import React, { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { toast } from '../../components/ui/use-toast';
import {
  SocialLinks,
  getSocialLinksForCurrentUser,
  updateSocialLinksForCurrentUser,
} from '../../services/socialLinksService';

// SocialLinksCard renders an editable card for managing social media URLs
// for the currently authenticated user inside any dashboard.
const SocialLinksCard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [links, setLinks] = useState<SocialLinks | null>(null);

  // Load existing social links for the current user when the card mounts.
  useEffect(() => {
    const loadLinks = async () => {
      try {
        const data = await getSocialLinksForCurrentUser();
        setLinks(data);
      } catch (error) {
        console.error('Failed to load social links for dashboard:', error);
        toast({
          title: 'Error',
          description: 'Failed to load social links',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadLinks();
  }, []);

  const handleChange = (field: keyof SocialLinks, value: string) => {
    setLinks((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleToggle = (
    field:
      | 'show_socials'
      | 'show_facebook'
      | 'show_instagram'
      | 'show_youtube'
      | 'show_linkedin'
      | 'show_tiktok'
  ) => {
    setLinks((prev) => {
      if (!prev) return prev;
      const current = prev[field];
      const isOn = current !== false;
      return { ...prev, [field]: !isOn };
    });
  };

  const handleSave = async () => {
    if (!links) return;

    setSaving(true);
    try {
      const updated = await updateSocialLinksForCurrentUser(links);
      setLinks(updated);
      toast({
        title: 'Saved',
        description: 'Your social media links have been updated',
      });
    } catch (error) {
      console.error('Error saving social links:', error);
      toast({
        title: 'Error',
        description: 'Failed to save social media links',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!links) {
    return null;
  }

   const showSocials = links.show_socials !== false;
   const showFacebook = links.show_facebook !== false;
   const showInstagram = links.show_instagram !== false;
   const showYoutube = links.show_youtube !== false;
   const showLinkedin = links.show_linkedin !== false;
   const showTiktok = links.show_tiktok !== false;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Social media</h3>
          <p className="text-sm text-gray-500">
            Optionally add links to your social media profiles. These will appear on your public page.
          </p>
          <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
            <button
              type="button"
              onClick={() => handleToggle('show_socials')}
              className={`relative inline-flex h-4 w-8 flex-shrink-0 items-center rounded-full transition-colors duration-200 ${
                showSocials ? 'bg-blue-600' : 'bg-gray-300'
              }`}
              aria-pressed={showSocials}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform duration-200 ${
                  showSocials ? 'translate-x-4' : 'translate-x-1'
                }`}
              />
            </button>
            <span>Show social media section on your public page</span>
          </div>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">Facebook</label>
            <button
              type="button"
              onClick={() => handleToggle('show_facebook')}
              className={`relative inline-flex h-4 w-8 flex-shrink-0 items-center rounded-full transition-colors duration-200 ${
                showFacebook ? 'bg-blue-600' : 'bg-gray-300'
              }`}
              aria-pressed={showFacebook}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform duration-200 ${
                  showFacebook ? 'translate-x-4' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          <input
            type="url"
            value={links.facebook_url || ''}
            onChange={(e) => handleChange('facebook_url', e.target.value)}
            placeholder="https://facebook.com/your-page"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">Instagram</label>
            <button
              type="button"
              onClick={() => handleToggle('show_instagram')}
              className={`relative inline-flex h-4 w-8 flex-shrink-0 items-center rounded-full transition-colors duration-200 ${
                showInstagram ? 'bg-blue-600' : 'bg-gray-300'
              }`}
              aria-pressed={showInstagram}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform duration-200 ${
                  showInstagram ? 'translate-x-4' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          <input
            type="url"
            value={links.instagram_url || ''}
            onChange={(e) => handleChange('instagram_url', e.target.value)}
            placeholder="https://instagram.com/your-profile"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">YouTube</label>
            <button
              type="button"
              onClick={() => handleToggle('show_youtube')}
              className={`relative inline-flex h-4 w-8 flex-shrink-0 items-center rounded-full transition-colors duration-200 ${
                showYoutube ? 'bg-blue-600' : 'bg-gray-300'
              }`}
              aria-pressed={showYoutube}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform duration-200 ${
                  showYoutube ? 'translate-x-4' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          <input
            type="url"
            value={links.youtube_url || ''}
            onChange={(e) => handleChange('youtube_url', e.target.value)}
            placeholder="https://youtube.com/@your-channel"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">LinkedIn</label>
            <button
              type="button"
              onClick={() => handleToggle('show_linkedin')}
              className={`relative inline-flex h-4 w-8 flex-shrink-0 items-center rounded-full transition-colors duration-200 ${
                showLinkedin ? 'bg-blue-600' : 'bg-gray-300'
              }`}
              aria-pressed={showLinkedin}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform duration-200 ${
                  showLinkedin ? 'translate-x-4' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          <input
            type="url"
            value={links.linkedin_url || ''}
            onChange={(e) => handleChange('linkedin_url', e.target.value)}
            placeholder="https://linkedin.com/in/your-profile"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">TikTok</label>
            <button
              type="button"
              onClick={() => handleToggle('show_tiktok')}
              className={`relative inline-flex h-4 w-8 flex-shrink-0 items-center rounded-full transition-colors duration-200 ${
                showTiktok ? 'bg-blue-600' : 'bg-gray-300'
              }`}
              aria-pressed={showTiktok}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform duration-200 ${
                  showTiktok ? 'translate-x-4' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          <input
            type="url"
            value={links.tiktok_url || ''}
            onChange={(e) => handleChange('tiktok_url', e.target.value)}
            placeholder="https://www.tiktok.com/@your-handle"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>
    </div>
  );
};

export default SocialLinksCard;
